import { Grid, makeStyles } from "@material-ui/core";
import LinearProgress from "@material-ui/core/LinearProgress";
import { School } from "@material-ui/icons";
import produce from "immer";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import {
  useApproveCandidateModuleMutation,
  useCancelCandidateModuleMutation,
  useGetExamSessionCandidateByIdQuery,
  useRebookCandidateModuleMutation,
  useRejectCandidateModuleMutation,
  useUpdateCandidateModuleMutation,
} from "../../../api/rtk/ExamSessionCandidateApi";
import { useGetAllTrainingProvidersQuery } from "../../../api/rtk/TrainingProviderApi";
import {
  ExamSessionCandidateExamState,
  ExamSessionCandidateExamViewModel,
} from "../../../model/examSessionCandidate/ExamSessionCandidateExamViewModel";
import { ExamSessionCandidateModuleViewModel } from "../../../model/examSessionCandidate/ExamSessionCandidateModuleViewModel";
import { OralExamOption } from "../../../model/OralExamOption";
import DefaultButtons from "../../common/buttons/DefaultButtons";
import TitleWithIcon from "../../common/TitleWithIcon";
import ExamSessionCandidateAttachmentList from "./ExamSessionCandidateAttachmentList";
import { manualStates } from "./ExamSessionCandidateConstants";
import ExamSessionCandidateModules from "./ExamSessionCandidateModules";
import ExamSessionCandidateInfo from "./ExamSessionCandidateInfo";
import { ExamType } from "../../../model/Exam";

const useStyles = makeStyles(() => ({
  infoRow: {
    marginBottom: "2rem",
  },
}));

const ExamSessionCandidateForm: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { examSessionCandidateId, moduleId } = useParams<{ examSessionCandidateId: string; moduleId: string }>();
  const [updateCandidateModule, { isLoading: isUpdateModuleLoading }] = useUpdateCandidateModuleMutation();
  const [approveCandidateModule, { isLoading: isApproveModuleLoading }] = useApproveCandidateModuleMutation();
  const [rejectCandidateModule, { isLoading: isRejectModuleLoading }] = useRejectCandidateModuleMutation();
  const { data: originalExamSessionCandidate, isFetching: isExamSessionCandidateFetching } =
    useGetExamSessionCandidateByIdQuery(examSessionCandidateId);
  const { data: trainingProviders, isFetching: isTrainingProvidersFetching } = useGetAllTrainingProvidersQuery();
  const [cancelModuleForCandidate, { isLoading: isCancellingModule }] = useCancelCandidateModuleMutation();
  const [rebookModuleForCandidate, { isLoading: isRebookingModule }] = useRebookCandidateModuleMutation();

  const isLoading =
    isExamSessionCandidateFetching ||
    isUpdateModuleLoading ||
    isTrainingProvidersFetching ||
    isApproveModuleLoading ||
    isRejectModuleLoading ||
    isCancellingModule ||
    isRebookingModule;

  const [examSessionCandidate, setExamSessionCandidate] = useState(originalExamSessionCandidate);

  const module = examSessionCandidate?.modules.find((module) => module.moduleId === moduleId);

  useEffect(() => {
    setExamSessionCandidate(() => originalExamSessionCandidate);
  }, [originalExamSessionCandidate]);

  if (isLoading) {
    return <LinearProgress variant="query" />;
  }

  if (examSessionCandidate === undefined || module === undefined || trainingProviders === undefined) {
    return <></>;
  }

  const handleManualExamStateChange = (moduleId: string, examId: string, state: ExamSessionCandidateExamState) =>
    changeExamProperties("state", state, moduleId, examId);

  const handleToggleExamManualState = (moduleId: string, examId: string) => {
    setExamSessionCandidate((prevExamSessionCandidate) =>
      produce(prevExamSessionCandidate, (draftExamSessionCandidate) => {
        const exam = draftExamSessionCandidate?.exams.find(
          (exam) => exam.moduleId === moduleId && exam.examId === examId
        );
        if (exam === undefined) {
          return;
        }

        exam.isManualState = !exam.isManualState;
        if (!exam.isManualState) {
          exam.state = ExamSessionCandidateExamState.Booked;
        } else {
          exam.state = manualStates.includes(exam.state) ? exam.state : manualStates[0];
        }

        exam.score = undefined;
        exam.grade = undefined;
      })
    );
  };

  const handleScoreChange = (moduleId: string, examId: string, score?: number | null) => {
    changeExamProperties("score", score, moduleId, examId);
  };

  const handleGradeChange = (moduleId: string, examId: string, grade?: number | null) => {
    changeExamProperties("grade", grade, moduleId, examId);
  };

  const changeExamProperties = (
    propertyName: keyof ExamSessionCandidateExamViewModel,
    value: any,
    moduleId: string,
    examId: string
  ) => {
    setExamSessionCandidate((prevExamSessionCandidate) =>
      produce(prevExamSessionCandidate, (draftExamSessionCandidate) => {
        const exam = draftExamSessionCandidate?.exams.find(
          (exam) => exam.examId === examId && exam.moduleId === moduleId
        );
        if (exam !== undefined) {
          exam[propertyName] = value as never;
        }
      })
    );
  };

  const handleSave = async () => {
    const exams = examSessionCandidate.exams
      .filter((exam) => exam.state !== ExamSessionCandidateExamState.Cancelled)
      .map((exam) => ({
        examId: exam.examId,
        score: exam.score,
        grade: exam.grade,
        state: exam.isManualState ? exam.state : undefined,
      }));
    updateCandidateModule({
      examSessionCandidateId,
      moduleId: module.moduleId,
      data: {
        publishResultsToEmployer: examSessionCandidate.publishResultsToEmployer,
        option: module.option,
        trainingProvider: module.trainingProvider,
        isRepeating: module.isRepeating,
        refundStatus: module.refundStatus,
        exams,
      },
    });
  };

  const changeModule = (propertyName: keyof ExamSessionCandidateModuleViewModel, value: never) => {
    setExamSessionCandidate((prevExamSessionCandidate) =>
      produce(prevExamSessionCandidate, (draftExamSessionCandidate) => {
        const currentModule = draftExamSessionCandidate?.modules.find((module) => module.moduleId === moduleId);
        if (currentModule !== undefined) {
          currentModule[propertyName] = value;
        }
      })
    );
  };

  const handleRefundStatusChange = (refundStatus: boolean) => {
    changeModule("refundStatus", refundStatus as never);
  };

  const handleOptionChange = (option: OralExamOption) => {
    changeModule("option", option as never);
  };

  const handleTrainingProviderChange = (trainingProviderKey: string) => {
    changeModule("trainingProvider", trainingProviderKey as never);
  };

  const handleIsRepeatingChange = (isRepeating: boolean) => {
    changeModule("isRepeating", isRepeating as never);
  };

  const handlePublishResultsToEmployerChange = (publishResultsToEmployer: boolean) => {
    setExamSessionCandidate((prevExamSessionCandidate) =>
      produce(prevExamSessionCandidate, (draftExamSessionCandidate) => {
        if (draftExamSessionCandidate) {
          draftExamSessionCandidate.publishResultsToEmployer = publishResultsToEmployer;
        }
      })
    );
  };

  const handleValidateModule = (approved: boolean) => {
    const data = {
      examSessionCandidateId,
      moduleId,
    };
    approved ? approveCandidateModule(data) : rejectCandidateModule(data);
  };

  const handleCancelModule = async (module: ExamSessionCandidateModuleViewModel) => {
    await cancelModuleForCandidate({
      examSessionCandidateId,
      moduleId: module.moduleId,
      comments: module.comments,
    }).unwrap();
  };

  const handleRebookModule = (module: ExamSessionCandidateModuleViewModel, rebookWithInvoice: boolean) => {
    rebookModuleForCandidate({ examSessionCandidateId, moduleId: module.moduleId, rebookWithInvoice });
  };

  const handleAddExam = (moduleId:string, examId: string, examType: ExamType) => {
    console.log('handleAddExam moduleId:', moduleId, 'examId:', examId);
     const newExamSessionCandidate = {...examSessionCandidate};
     newExamSessionCandidate.exams = [...newExamSessionCandidate.exams, {
       examId: examId,
       moduleId: moduleId,
       state: ExamSessionCandidateExamState.Cancelled,
       type: examType
     } as ExamSessionCandidateExamViewModel];
    setExamSessionCandidate(newExamSessionCandidate);
  };


  return (
    <React.Fragment>
      <Grid container={true} spacing={0} alignItems="baseline" className={classes.infoRow}>
        <Grid item={true} xs={12} sm={6}>
          <TitleWithIcon title={`${examSessionCandidate.firstName} ${examSessionCandidate.lastName}`} variant="h4">
            <School />
          </TitleWithIcon>
        </Grid>
        <Grid item={true} xs={12} sm={6}>
          <DefaultButtons onSave={handleSave} onClose={() => history.goBack()} isDeleteButtonVisible={false} />
        </Grid>
      </Grid>

      <div className={classes.infoRow}>
        <ExamSessionCandidateInfo
          examSessionCandidate={examSessionCandidate}
          module={module}
          trainingProviders={trainingProviders}
          refundStatusChange={handleRefundStatusChange}
          optionChange={handleOptionChange}
          trainingProviderChange={handleTrainingProviderChange}
          isRepeatingChange={handleIsRepeatingChange}
          publishResultsToEmployerChange={handlePublishResultsToEmployerChange}
          validateModule={handleValidateModule}
        />
      </div>

      <div className={classes.infoRow}>
        <ExamSessionCandidateModules
          examSessionCandidate={examSessionCandidate}
          moduleId={moduleId}
          manualExamStateChange={handleManualExamStateChange}
          toggleExamManualState={handleToggleExamManualState}
          examScoreChange={handleScoreChange}
          examGradeChange={handleGradeChange}
          onCancelModule={handleCancelModule}
          onRebookModule={handleRebookModule}
          onAddExam={handleAddExam}
        />
      </div>

      <ExamSessionCandidateAttachmentList examSessionCandidateId={examSessionCandidateId} moduleId={moduleId} />
    </React.Fragment>
  );
};

export default ExamSessionCandidateForm;
